// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  BlankStoryCreate,
  ErrorResponse,
  PatchedStorySlideUpdate,
  PatchedStoryUpdate,
  StoriesListParams,
  StoryBookInterestResponse,
  StoryDeleteResponse,
  StoryDetail,
  StoryDetailResponse,
  StoryGeneration,
  StoryGenerationResponse,
  StoryListResponse,
  StorySlideUpdateResponse,
  StoryUpdateResponse,
  ValidationErrorResponse,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Retrieve a paginated list of stories belonging to the authenticated user. Supports filtering by status, favorites, archived state, and reader profile. Results can be sorted by creation date or last read date.
 * @summary List user's stories
 */
export const storiesList = (
  params?: StoriesListParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryListResponse>(
    { url: `/api/stories/`, method: "GET", params, signal },
    options
  );
};

export const getStoriesListQueryKey = (params?: StoriesListParams) => {
  return [`/api/stories/`, ...(params ? [params] : [])] as const;
};

export const getStoriesListQueryOptions = <
  TData = Awaited<ReturnType<typeof storiesList>>,
  TError = unknown
>(
  params?: StoriesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesList>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStoriesListQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof storiesList>>> = ({
    signal,
  }) => storiesList(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof storiesList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StoriesListQueryResult = NonNullable<
  Awaited<ReturnType<typeof storiesList>>
>;
export type StoriesListQueryError = unknown;

export function useStoriesList<
  TData = Awaited<ReturnType<typeof storiesList>>,
  TError = unknown
>(
  params: undefined | StoriesListParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesList>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesList>>,
          TError,
          Awaited<ReturnType<typeof storiesList>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesList<
  TData = Awaited<ReturnType<typeof storiesList>>,
  TError = unknown
>(
  params?: StoriesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesList>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesList>>,
          TError,
          Awaited<ReturnType<typeof storiesList>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesList<
  TData = Awaited<ReturnType<typeof storiesList>>,
  TError = unknown
>(
  params?: StoriesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesList>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List user's stories
 */

export function useStoriesList<
  TData = Awaited<ReturnType<typeof storiesList>>,
  TError = unknown
>(
  params?: StoriesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesList>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStoriesListQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieve a single story with all slides, characters, and metadata. Automatically updates the last_read_at timestamp when accessed. Only returns stories belonging to the authenticated user.
 * @summary Get story details
 */
export const storiesDetail = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryDetailResponse>(
    { url: `/api/stories/${id}/`, method: "GET", signal },
    options
  );
};

export const getStoriesDetailQueryKey = (id: number) => {
  return [`/api/stories/${id}/`] as const;
};

export const getStoriesDetailQueryOptions = <
  TData = Awaited<ReturnType<typeof storiesDetail>>,
  TError = ErrorResponse
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesDetail>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStoriesDetailQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof storiesDetail>>> = ({
    signal,
  }) => storiesDetail(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof storiesDetail>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StoriesDetailQueryResult = NonNullable<
  Awaited<ReturnType<typeof storiesDetail>>
>;
export type StoriesDetailQueryError = ErrorResponse;

export function useStoriesDetail<
  TData = Awaited<ReturnType<typeof storiesDetail>>,
  TError = ErrorResponse
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesDetail>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesDetail>>,
          TError,
          Awaited<ReturnType<typeof storiesDetail>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesDetail<
  TData = Awaited<ReturnType<typeof storiesDetail>>,
  TError = ErrorResponse
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesDetail>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesDetail>>,
          TError,
          Awaited<ReturnType<typeof storiesDetail>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesDetail<
  TData = Awaited<ReturnType<typeof storiesDetail>>,
  TError = ErrorResponse
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesDetail>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get story details
 */

export function useStoriesDetail<
  TData = Awaited<ReturnType<typeof storiesDetail>>,
  TError = ErrorResponse
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof storiesDetail>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStoriesDetailQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Record user's interest in having this story published as a physical book. This is a one-way action - interest cannot be removed once expressed. Only completed stories are eligible for book interest. If the user has already expressed interest, this endpoint is idempotent and returns success.
 * @summary Express interest in story as physical book
 */
export const storiesBookInterest = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryBookInterestResponse>(
    { url: `/api/stories/${id}/book-interest/`, method: "POST", signal },
    options
  );
};

export const getStoriesBookInterestMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesBookInterest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesBookInterest>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["storiesBookInterest"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesBookInterest>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return storiesBookInterest(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesBookInterestMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesBookInterest>>
>;

export type StoriesBookInterestMutationError = ErrorResponse;

/**
 * @summary Express interest in story as physical book
 */
export const useStoriesBookInterest = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesBookInterest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesBookInterest>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationOptions = getStoriesBookInterestMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Permanently delete a story and all associated slides. This is a hard delete and cannot be undone. Only the story owner can delete their stories.
 * @summary Delete a story
 */
export const storiesDelete = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<StoryDeleteResponse>(
    { url: `/api/stories/${id}/delete/`, method: "DELETE" },
    options
  );
};

export const getStoriesDeleteMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesDelete>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesDelete>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["storiesDelete"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesDelete>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return storiesDelete(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesDelete>>
>;

export type StoriesDeleteMutationError = ErrorResponse;

/**
 * @summary Delete a story
 */
export const useStoriesDelete = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesDelete>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesDelete>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationOptions = getStoriesDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Update story title, favorite status, archive status, tags, or public sharing settings. Only completed stories can be edited. When making a story public, a unique share slug is automatically generated if one doesn't exist.
 * @summary Update story metadata
 */
export const storiesUpdate = (
  id: number,
  patchedStoryUpdate: PatchedStoryUpdate,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<StoryUpdateResponse>(
    {
      url: `/api/stories/${id}/update/`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: patchedStoryUpdate,
    },
    options
  );
};

export const getStoriesUpdateMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesUpdate>>,
    TError,
    { id: number; data: PatchedStoryUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesUpdate>>,
  TError,
  { id: number; data: PatchedStoryUpdate },
  TContext
> => {
  const mutationKey = ["storiesUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesUpdate>>,
    { id: number; data: PatchedStoryUpdate }
  > = (props) => {
    const { id, data } = props ?? {};

    return storiesUpdate(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesUpdate>>
>;
export type StoriesUpdateMutationBody = PatchedStoryUpdate;
export type StoriesUpdateMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Update story metadata
 */
export const useStoriesUpdate = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesUpdate>>,
    TError,
    { id: number; data: PatchedStoryUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesUpdate>>,
  TError,
  { id: number; data: PatchedStoryUpdate },
  TContext
> => {
  const mutationOptions = getStoriesUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Update the content of an individual slide within a story. Can modify content_data (text blocks, images, interaction layers) and parent_prompt. Only slides from completed stories can be edited. Content data must follow the structured format with ordered content blocks.
 * @summary Update a story slide
 */
export const storySlidesUpdate = (
  storyId: number,
  slideNumber: number,
  patchedStorySlideUpdate: PatchedStorySlideUpdate,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<StorySlideUpdateResponse>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: patchedStorySlideUpdate,
    },
    options
  );
};

export const getStorySlidesUpdateMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storySlidesUpdate>>,
    TError,
    { storyId: number; slideNumber: number; data: PatchedStorySlideUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storySlidesUpdate>>,
  TError,
  { storyId: number; slideNumber: number; data: PatchedStorySlideUpdate },
  TContext
> => {
  const mutationKey = ["storySlidesUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storySlidesUpdate>>,
    { storyId: number; slideNumber: number; data: PatchedStorySlideUpdate }
  > = (props) => {
    const { storyId, slideNumber, data } = props ?? {};

    return storySlidesUpdate(storyId, slideNumber, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorySlidesUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storySlidesUpdate>>
>;
export type StorySlidesUpdateMutationBody = PatchedStorySlideUpdate;
export type StorySlidesUpdateMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Update a story slide
 */
export const useStorySlidesUpdate = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storySlidesUpdate>>,
    TError,
    { storyId: number; slideNumber: number; data: PatchedStorySlideUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storySlidesUpdate>>,
  TError,
  { storyId: number; slideNumber: number; data: PatchedStorySlideUpdate },
  TContext
> => {
  const mutationOptions = getStorySlidesUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * 
        Create a blank story with spread-based layout ready for editing.

        The story will have a proper print layout:
        - Slide 1: Cover spread (page 0, front cover + back cover)
        - Slide 2: Front inside spread (page 1)
        - Slides 3-N: Content spreads (pages 2-3, 4-5, ..., both editable)
        - Slide N+1: Back inside spread (last page)

        Each spread gets a pre-created background layer with status COMPLETE,
        ready for image upload or generation.

        Valid total_pages: 30, 32, or 34

        The story status is set to COMPLETE so it can be edited immediately.
        
 * @summary Create blank story
 */
export const storiesBlankCreate = (
  blankStoryCreate: BlankStoryCreate,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryDetail>(
    {
      url: `/api/stories/blank/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: blankStoryCreate,
      signal,
    },
    options
  );
};

export const getStoriesBlankCreateMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesBlankCreate>>,
    TError,
    { data: BlankStoryCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesBlankCreate>>,
  TError,
  { data: BlankStoryCreate },
  TContext
> => {
  const mutationKey = ["storiesBlankCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesBlankCreate>>,
    { data: BlankStoryCreate }
  > = (props) => {
    const { data } = props ?? {};

    return storiesBlankCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesBlankCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesBlankCreate>>
>;
export type StoriesBlankCreateMutationBody = BlankStoryCreate;
export type StoriesBlankCreateMutationError = ErrorResponse;

/**
 * @summary Create blank story
 */
export const useStoriesBlankCreate = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesBlankCreate>>,
    TError,
    { data: BlankStoryCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesBlankCreate>>,
  TError,
  { data: BlankStoryCreate },
  TContext
> => {
  const mutationOptions = getStoriesBlankCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Creates a new story and initiates async generation process. The story will be created with PENDING status and a Celery task will be enqueued to generate the story content. The user will receive a push notification when complete.
 * @summary Generate a new story
 */
export const storiesGenerateCreate = (
  storyGeneration: StoryGeneration,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryGenerationResponse>(
    {
      url: `/api/stories/generate/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: storyGeneration,
      signal,
    },
    options
  );
};

export const getStoriesGenerateCreateMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesGenerateCreate>>,
    TError,
    { data: StoryGeneration },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesGenerateCreate>>,
  TError,
  { data: StoryGeneration },
  TContext
> => {
  const mutationKey = ["storiesGenerateCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesGenerateCreate>>,
    { data: StoryGeneration }
  > = (props) => {
    const { data } = props ?? {};

    return storiesGenerateCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesGenerateCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesGenerateCreate>>
>;
export type StoriesGenerateCreateMutationBody = StoryGeneration;
export type StoriesGenerateCreateMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Generate a new story
 */
export const useStoriesGenerateCreate = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesGenerateCreate>>,
    TError,
    { data: StoryGeneration },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesGenerateCreate>>,
  TError,
  { data: StoryGeneration },
  TContext
> => {
  const mutationOptions = getStoriesGenerateCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
